import { Fragment } from 'react'
import { Icon } from '../../components/ui/Icon'
import type { RfqStatus } from './domain'
import { RFQ_STEPS, resolveRfqStepIndex, stepState } from './domain'

export interface StatusStepperProps {
  readonly status: RfqStatus
  /** UID-193 — `'compact'` adds the `rfq-status-stepper--compact` modifier
   * class (smaller dots, same hairline line connector as the default
   * variant) for the RFQ list row's decorative reinforcement. Defaults to
   * `'default'`, the unchanged detail-page treatment. */
  readonly variant?: 'default' | 'compact'
}

/**
 * RFQ-card status stepper (screens.md §14, components.md "Status stepper" —
 * "RFQ submitted → sourcing → quoted"). An ordered list with a
 * visually-hidden state word per step and `aria-current="step"` on the
 * active one, matching the Order-detail stepper's accessibility treatment
 * (`orders/StatusStepper.tsx`) rather than only a visual dot-fill.
 *
 * The connector between steps is a plain line (`.rfq-status-connector`),
 * colored from the step it ARRIVES AT — navy when that step is
 * completed/current, light grey when it's upcoming. This is RFQ's OWN
 * connector, deliberately not shared with Orders' `.status-step-arrow`
 * (UID-317's arrow-icon slot) — the two verticals' stepper designs have
 * diverged.
 *
 * The connector renders as a decorative SIBLING `<li>` of the step `<li>`,
 * not a child of it. `orders.css`'s unscoped `.status-step { flex: 1 }`
 * partitions the `<ol>` into thirds when the connector is nested inside a
 * step, so each connector can only absorb slack inside its own third — the
 * structural cause of the connectors reading too short / the stepper not
 * filling the card. As a sibling `<li>` with its own `flex: 1`
 * (`.rfq-status-connector`, rfq.css), the connector absorbs ALL of the
 * row's slack instead, matching the design source. `aria-hidden="true"`
 * keeps it out of the accessibility tree, so the exposed `listitem` count
 * stays exactly `RFQ_STEPS.length` (3) despite the `<ol>` having 5 real
 * `<li>` DOM children for a 3-step stepper.
 *
 * `expired` renders nothing here — the card itself carries the
 * muted/"Re-request quote" treatment (screens.md §14) instead of a stepper
 * position.
 */
export function StatusStepper({ status, variant = 'default' }: StatusStepperProps) {
  const currentIndex = resolveRfqStepIndex(status)
  if (currentIndex === null) return null

  const className =
    variant === 'compact' ? 'rfq-status-stepper rfq-status-stepper--compact' : 'rfq-status-stepper'

  return (
    <ol className={className} aria-label="RFQ status">
      {RFQ_STEPS.map((step, index) => {
        const state = stepState(index, currentIndex)
        return (
          <Fragment key={step.id}>
            <li
              className={`status-step status-step-${state}`}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="status-step-dot" aria-hidden="true">
                <Icon name="check" />
              </span>
              <span className="status-step-label">{step.label}</span>
              <span className="sr-only"> — {state}</span>
            </li>
            {index < RFQ_STEPS.length - 1 && (
              <li
                className={`rfq-status-connector rfq-status-connector--${
                  stepState(index + 1, currentIndex) === 'upcoming' ? 'upcoming' : 'done'
                }`}
                aria-hidden="true"
              />
            )}
          </Fragment>
        )
      })}
    </ol>
  )
}
